import * as React from 'react';

import Validatable from './Validatable';
import ValidatorContext from '../../context/ValidatorContext';

import { expandDataProps } from 'utils/helper/common';
import { scrollToMissingField } from 'utils/page';

import type { Data, FormValidator } from 'types';

import type { SyntheticEvent } from 'react';

type Props = {
  nestedForm?: boolean,
  className?: string,
  children: React.ReactNode,
  onSubmit?: (event: SyntheticEvent<HTMLFormElement>, options?: any) => void | Promise<void>,
  onValidationErrors?: () => void,
  data?: Data
};

type State = {
  fields: Array<Validatable>
};

class Form extends React.Component<Props, State> {
  state = {
    fields: [] as Array<Validatable>
  };

  getValidatorContext() {
    return {
      registerValidators: this.registerValidators,
      unregisterValidators: this.unregisterValidators
    };
  }

  registerValidators = (component: Validatable, validators: Array<FormValidator>): void => {
    if (component && validators && validators.length > 0) {
      this.state.fields.push(component);
    }
  };

  unregisterValidators = (component: Validatable): void => {
    if (!component) {
      return;
    }

    const index = this.state.fields.indexOf(component);

    if (index < 0) {
      return;
    }

    this.state.fields.splice(index, 1);
  };

  resetValidation(): void {
    this.state.fields.forEach((field) => field.resetValidation());
  }

  isFormValid() {
    return this.state.fields.reduce((valid, field) => field.validate() && valid, true);
  }

  handleSubmit = (
    event: SyntheticEvent<HTMLFormElement>,
    options: any | undefined | null = null
  ): unknown => {
    const { onSubmit, onValidationErrors } = this.props;

    const isFormValid = this.isFormValid();
    event.preventDefault();
    event.stopPropagation();

    if (isFormValid && onSubmit) {
      this.resetValidation();
      return onSubmit(event, options);
    }

    if (isFormValid) {
      return true;
    }

    scrollToMissingField(true, this.props.className);
    if (onValidationErrors) {
      onValidationErrors();
    }
    return false;
  };

  render() {
    const { children, data, nestedForm, className } = this.props;
    const formProps = {
      className,
      ...expandDataProps(data)
    };

    return (
      <ValidatorContext.Provider value={this.getValidatorContext()}>
        {
          nestedForm ?
            <div {...formProps}>
              {children}
            </div> :
            <form
              {...formProps}
              onSubmit={this.handleSubmit}
            >
              {children}
            </form>
        }
      </ValidatorContext.Provider>
    );
  }
}

export default Form;
